/**
 * Shared card primitives for the list screens.
 *
 * Clients and Requests each grew their own copy of these while the design
 * sweep was converting tables to cards, and they had already drifted — two
 * avatar sizes (40 vs 34) for the same visual role. Staff would have been the
 * third copy, so they live here instead.
 *
 * `size` keeps both existing call sites honest rather than forcing one of them
 * to change appearance in a refactor commit.
 */

import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";

/** "Dipalee Suryavanshi" → "DS". Falls back to "?" for a blank name. */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0]![0]! + (parts[1]?.[0] ?? "")).toUpperCase();
}

/** Circular initials avatar. */
export function Avatar({
  name,
  size = 40,
}: {
  name: string | null | undefined;
  size?: number;
}): ReactNode {
  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--rp-color-surface-2)",
        color: "var(--rp-color-text-muted)",
        fontSize: size <= 34 ? 12.5 : 13.5,
        fontWeight: 700,
      }}
    >
      {initials(name)}
    </div>
  );
}

/** Right-pointing chevron — "this whole card is a link". */
export const Chevron = (): ReactNode => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ color: "var(--rp-color-text-muted)", flexShrink: 0 }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/**
 * The card box itself. Pair with className="rp-clientcard" so the contact
 * buttons inside get the container query that collapses them to icons.
 *
 * Wraps by DEFAULT. Every card that put a real control on the right — the
 * assign-agent select, the Staff action cluster, the Assign DID form — shipped
 * an overflow bug because this did not, and each one had to be found by
 * resizing the window until something slid off the edge. Opting out is rare
 * and explicit (`flexWrap: "nowrap"`); forgetting to opt in was neither.
 *
 * A card that wraps also wants a floor on its flexible column — `minWidth: 220`
 * or so — otherwise the column shrinks to nothing before the row ever breaks.
 */
export const cardShell: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
  rowGap: 10,
  padding: "12px 14px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-lg)",
  background: "var(--rp-color-surface)",
  boxShadow: "var(--rp-shadow-sm)",
};

/** Vertical stack of cards — the standard list body. */
export const cardList: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

/**
 * Expand / minimise glyph — a plus that loses its vertical stroke when open,
 * so the sign itself says which way the click goes and the label need not.
 */
export function ExpandSign({ open }: { open: boolean }): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      // 18, not 12. At 12px with a 2px stroke this was a 10px mark on a
      // section header — legible, but reading as decoration rather than as the
      // control that decides whether the section is visible at all.
      width="18"
      height="18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <line x1="3" y1="8" x2="13" y2="8" />
      {!open && <line x1="8" y1="3" x2="8" y2="13" />}
    </svg>
  );
}

/**
 * A titled section that collapses to its header.
 *
 * Own state rather than lifted: which sections you left open is scratch
 * preference for the screen in front of you, and threading a key space through
 * every caller to remember it past a navigation buys nothing.
 *
 * `defaultOpen` is how a screen says which sections earn their space — a
 * settings page opens the details and collapses the rarely-touched ones.
 */
export function Collapsible({
  title,
  subtitle,
  defaultOpen = true,
  headingLevel: Heading = "h2",
  right,
  children,
}: {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  headingLevel?: "h2" | "h3";
  /** Rendered on the header's right — outside the toggle, so a control there
   *  does not also collapse the section. */
  right?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
            padding: 0,
            border: "none",
            background: "none",
            font: "inherit",
            color: "inherit",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <ExpandSign open={open} />
          <Heading style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
            {title}
          </Heading>
        </button>
        {right}
      </div>
      {open && (
        <>
          {subtitle && (
            <p
              style={{
                color: "var(--rp-color-text-muted)",
                fontSize: 13,
                margin: "8px 0 0",
              }}
            >
              {subtitle}
            </p>
          )}
          <div style={{ marginTop: 14 }}>{children}</div>
        </>
      )}
    </div>
  );
}
